import type { CameraConnectionIssueReason } from 'src/services/posthogEvents';
import { StateCreator } from 'zustand';

import { CameraData } from 'src/models/CameraData.model';
import { BackendStatusEnum } from 'src/models/GalenData/DeviceDataView.model';

import { RemoteBoundStoreState } from './useRemoteBoundStore';

interface StateTypes {
  isCapturing: boolean;
  isCanceling: boolean;
  leftUrl: string;
  rightUrl: string;
  cameraData: CameraData | undefined;
  backendStatus: BackendStatusEnum | undefined;
  hasStarted: boolean;
  leftImageAttempts: number;
  rightImageAttempts: number;
  leftImageSubmission: number;
  rightImageSubmission: number;
  cameraConnectionIssueReason: CameraConnectionIssueReason | undefined;
  captureInterruptedByConnectionIssue: boolean;
}

const initialCameraState: StateTypes = {
  isCapturing: false,
  isCanceling: false,
  leftUrl: '',
  rightUrl: '',
  cameraData: undefined,
  backendStatus: undefined,
  hasStarted: false,
  leftImageAttempts: 0,
  rightImageAttempts: 0,
  leftImageSubmission: 0,
  rightImageSubmission: 0,
  cameraConnectionIssueReason: undefined,
  captureInterruptedByConnectionIssue: false,
};
type ActionsTypes = {
  setIsCapturing: (isCapturing: boolean) => void;
  setIsCanceling: (isCanceling: boolean) => void;
  setLeftUrl: (leftUrl: string) => void;
  setRightUrl: (rightUrl: string) => void;
  setCameraData: (cameraData: CameraData | undefined) => void;
  setBackendStatus: (backendStatus: BackendStatusEnum | undefined) => void;
  resetCameraState: () => void;
  setHasStarted: (hasStarted: boolean) => void;
  setLeftImageAttempts: (leftImageAttempts: number) => void;
  setRightImageAttempts: (rightImageAttempts: number) => void;
  setLeftImageSubmission: (leftImageSubmission: number) => void;
  setRightImageSubmission: (rightImageSubmission: number) => void;
  setCameraConnectionIssueReason: (
    reason: CameraConnectionIssueReason | undefined,
  ) => void;
  setCaptureInterruptedByConnectionIssue: (interrupted: boolean) => void;
  incrementLeftImageAttempts: () => void;
  incrementRightImageAttempts: () => void;
};

export type CameraSliceTypes = StateTypes & ActionsTypes;

export const createCameraSlice: StateCreator<
  RemoteBoundStoreState,
  [],
  [],
  CameraSliceTypes
> = (set, get) => ({
  ...initialCameraState,
  setIsCapturing: (isCapturing: boolean) => set({ isCapturing }),
  setIsCanceling: (isCanceling: boolean) => set({ isCanceling }),
  setLeftUrl: (leftUrl: string) => set({ leftUrl }),
  setRightUrl: (rightUrl: string) => set({ rightUrl }),
  setCameraData: (cameraData: CameraData | undefined) => set({ cameraData }),
  setBackendStatus: (backendStatus: BackendStatusEnum | undefined) =>
    set({ backendStatus }),
  resetCameraState: () => set(initialCameraState),
  setHasStarted: (hasStarted: boolean) => set({ hasStarted }),
  setLeftImageAttempts: (leftImageAttempts: number) =>
    set({ leftImageAttempts }),
  setRightImageAttempts: (rightImageAttempts: number) =>
    set({ rightImageAttempts }),
  setLeftImageSubmission: (leftImageSubmission: number) =>
    set({ leftImageSubmission }),
  setRightImageSubmission: (rightImageSubmission: number) =>
    set({ rightImageSubmission }),
  setCameraConnectionIssueReason: (reason) => {
    if (!reason) {
      set({ cameraConnectionIssueReason: undefined });
      return;
    }

    const wasCapturing = get().isCapturing;
    set({
      cameraConnectionIssueReason: reason,
      isCapturing: false,
      isCanceling: false,
      hasStarted: false,
      ...(wasCapturing ? { captureInterruptedByConnectionIssue: true } : {}),
    });
  },
  setCaptureInterruptedByConnectionIssue: (interrupted) =>
    set({ captureInterruptedByConnectionIssue: interrupted }),
  incrementLeftImageAttempts: () =>
    set((state) => ({ leftImageAttempts: state.leftImageAttempts + 1 })),
  incrementRightImageAttempts: () =>
    set((state) => ({ rightImageAttempts: state.rightImageAttempts + 1 })),
});
